import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../OnboardingContext';
import { Search, Building2, ChevronDown, Check, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FlagImg } from '@/components/ui/CountryFlag';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { countries, countriesSorted } from '@/data/countries';
import { isCompanyName } from '@/utils/companyDetection';
import { supabase } from '@/integrations/supabase/client';
// Company statuses that prevent onboarding
const REJECTED_STATUSES = ['Dissolved', 'Liquidation', 'Administration', 'Struck Off', 'Inactive'];

export function CorporateCompanySearchStep() {
  const { t } = useTranslation();
  const { updateData, goToNextStep, data } = useOnboarding();

  // Restore previously entered values from context if available
  const savedCompany = data.corporateCompany;
  // Single smart query field — auto-classifies into name / regNo / vatNo
  const initialQuery = savedCompany?.vatNumber || savedCompany?.registrationNumber || savedCompany?.name || '';
  const [selectedCountry, setSelectedCountry] = useState<string>(savedCompany?.countryCode || '');
  const [isDetectingCountry, setIsDetectingCountry] = useState(!savedCompany?.countryCode);
  const [query, setQuery] = useState<string>(initialQuery);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const [rejectedCompany, setRejectedCompany] = useState<{
    name: string;
    status: string;
    registrationNumber: string;
  } | null>(null);

  // Auto-detect country from IP only if we don't already have a saved country
  useEffect(() => {
    if (savedCompany?.countryCode) {
      // Already restored from context — no detection needed
      return;
    }
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const geoData = await response.json();
        
        if (geoData?.country_code) {
          const detectedCountry = countries.find(c => c.code === geoData.country_code);
          setSelectedCountry(detectedCountry ? geoData.country_code : 'GB');
        } else {
          setSelectedCountry('GB');
        }
      } catch {
        setSelectedCountry('GB');
      } finally {
        setIsDetectingCountry(false);
      }
    };

    detectCountry();
  }, []);

  const selectedCountryData = countries.find(c => c.code === selectedCountry);

  const filteredCountries = countrySearch
    ? countriesSorted.filter(c => 
        c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.code.toLowerCase().includes(countrySearch.toLowerCase())
      )
    : countriesSorted;

  // Known EU/UK/CH VAT country prefixes — used to detect if input is a VAT number
  const VAT_PREFIXES = new Set(['AT','BE','BG','CY','CZ','DE','DK','EE','EL','GR','ES','FI','FR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','GB','XI','CH','NO']);

  /** Classify a single user-typed value into ordered search attempts. */
  const buildAttempts = (raw: string): Array<{ regNo?: string; vatNo?: string; name?: string; label: string }> => {
    const value = raw.trim();
    if (!value) return [];
    const compact = value.replace(/\s+/g, '').toUpperCase();
    const attempts: Array<{ regNo?: string; vatNo?: string; name?: string; label: string }> = [];

    // Pattern: 2-letter country prefix + alphanumerics → likely VAT
    const vatMatch = /^([A-Z]{2})([A-Z0-9]{4,})$/.exec(compact);
    if (vatMatch && VAT_PREFIXES.has(vatMatch[1])) {
      attempts.push({ vatNo: compact, label: 'vatNo' });
      // The numeric tail is often identical to the registration number
      attempts.push({ regNo: vatMatch[2], label: 'vatNo-as-regNo' });
    }

    // Pattern: pure digits or alphanumerics with no spaces → likely a registration number
    if (!vatMatch && /^[A-Z0-9\-./]{4,15}$/.test(compact)) {
      attempts.push({ regNo: value, label: 'regNo' });
    }

    // Always also try the raw value as a name (covers Cyrillic / multi-word company names)
    attempts.push({ name: value, label: 'name' });

    // De-duplicate by JSON shape
    const seen = new Set<string>();
    return attempts.filter(a => {
      const key = JSON.stringify({ r: a.regNo, v: a.vatNo, n: a.name });
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const handleSearch = async () => {
    const raw = query.trim();
    if (!raw) return;

    setIsSearching(true);
    setRejectedCompany(null);
    setSearchNotFound(false);

    const attempts = buildAttempts(raw);

    try {
      let searchResult: any = null;
      let companies: any[] = [];

      for (const attempt of attempts) {
        console.log('[CreditSafe] Search attempt:', attempt.label, attempt);
        const { data: result, error: searchError } = await supabase.functions.invoke('creditsafe-company', {
          body: {
            action: 'search',
            countryCode: selectedCountry,
            regNo: attempt.regNo,
            vatNo: attempt.vatNo,
            name: attempt.name,
          }
        });

        if (searchError || !result?.success) {
          console.warn('[CreditSafe] Attempt failed:', attempt.label, searchError || result?.error);
          continue;
        }

        const found = result.data?.companies || [];
        if (found.length > 0) {
          searchResult = result;
          companies = found;
          console.log(`[CreditSafe] Match on attempt "${attempt.label}" — ${found.length} result(s)`);
          break;
        }
      }

      if (!searchResult || companies.length === 0) {
        setSearchNotFound(true);
        setIsSearching(false);
        return;
      }

      // Get the first matching company
      const foundCompany = companies[0];
      
      // Check if company status is rejected
      if (REJECTED_STATUSES.includes(foundCompany.status)) {
        setRejectedCompany({
          name: foundCompany.name,
          status: foundCompany.status,
          registrationNumber: foundCompany.registrationNumber,
        });
        setIsSearching(false);
        return;
      }

      // Attempt full company report (directors, shareholders, full address, etc.)
      const { data: reportResult, error: reportError } = await supabase.functions.invoke('creditsafe-company', {
        body: {
          action: 'report',
          connectId: foundCompany.connectId,
          countryCode: selectedCountry,
          regNo: foundCompany.registrationNumber || query.trim(),
          vatNo: foundCompany.vatNumber,
        }
      });

      // Unwrap report result — prefer .data (edge function wraps as { success, data })
      // but also handle case where invoke returns the company object directly
      const reportBody = reportResult?.data || reportResult;
      const reportOk = reportResult?.success && !reportBody?.reportUnavailable;
      // Even when the full CreditSafe report is unavailable, the edge function may return
      // enrichment data from fallback sources (e.g. nif.pt for PT companies).
      const companyData = reportOk ? reportBody : null;
      // Partial enrichment from fallback (nif.pt, localSolutions, etc.) — available even on 403
      const partialData = (!reportOk && reportResult?.success) ? reportBody : null;
      console.log('[Onboarding] Report merge:', { reportError: !!reportError, reportOk, hasIncDate: !!companyData?.incorporationDate, companyType: companyData?.companyType });

      // Build address — prefer full report, fall back to partial enrichment, then search-level address
      const searchAddr = foundCompany.fullAddress || null;
      // Report can return address as registeredAddress or address object
      const reportAddr = companyData?.registeredAddress || companyData?.address || partialData?.registeredAddress || null;
      const ADDR_INVALID = ['Click to edit', 'NotSet', 'None', 'N/A'];
      const cleanAddr = (v: string | undefined | null) => (!v || ADDR_INVALID.includes(v.trim())) ? '' : v.trim();
      const addrStreet = cleanAddr(reportAddr?.street) || cleanAddr(searchAddr?.street) || '';
      const addrCity   = cleanAddr(reportAddr?.city)   || cleanAddr(searchAddr?.city)   || '';
      const addrPost   = cleanAddr(reportAddr?.postalCode) || cleanAddr(reportAddr?.postCode) || cleanAddr(searchAddr?.postalCode) || '';
      // Resolve country to full name (handles ISO2 codes like "PT" → "Portugal")
      const rawAddrCountry = cleanAddr(reportAddr?.country) || cleanAddr(searchAddr?.country) || selectedCountry || '';
      const addrCountry = rawAddrCountry
        ? (rawAddrCountry.length === 2
            ? (countries.find(c => c.code === rawAddrCountry.toUpperCase())?.name || rawAddrCountry)
            : rawAddrCountry)
        : (selectedCountryData?.name || '');

      const registeredAddressString = [addrStreet, addrCity, addrPost].filter(Boolean).join(', ');

      // For every field: prefer full report data, fall back to partial enrichment, then search-level data
      const INVALID_VALUES = ['NotSet', 'None', 'Unknown', 'N/A', 'n/a', ''];
      const sanitize = (v: string | undefined | null) => (!v || INVALID_VALUES.includes(v.trim())) ? '' : v.trim();

      const mergedName = sanitize(companyData?.name) || sanitize(partialData?.name) || sanitize(foundCompany.name) || '';
      const mergedRegNo = sanitize(companyData?.registrationNumber) || sanitize(foundCompany.registrationNumber) || query.trim();
      const mergedStatus = sanitize(companyData?.status) || sanitize(partialData?.status) || sanitize(foundCompany.status) || 'Active';
      const mergedIncorporationDate = sanitize(companyData?.incorporationDate)
        || sanitize(partialData?.incorporationDate)
        || sanitize(foundCompany.incorporationDate)
        || sanitize(foundCompany.dateOfIncorporation)
        || sanitize(foundCompany.dateOfRegistration)
        || sanitize(foundCompany.registrationDate)
        || sanitize(foundCompany.foundingDate)
        || sanitize(foundCompany.operationsStartDate)
        || sanitize(foundCompany.dateOfCommencement)
        || sanitize(foundCompany.formationDate)
        || '';
      const mergedCompanyType = sanitize(companyData?.companyType) || sanitize(partialData?.companyType) || sanitize(foundCompany.companyType) || sanitize(foundCompany.legalForm?.description) || sanitize(foundCompany.legalForm?.name) || sanitize(foundCompany.legalForm?.code) || sanitize(foundCompany.type) || '';
      const mergedMainActivity = sanitize(companyData?.mainActivity) || sanitize(partialData?.mainActivity) || '';
      // VAT number from CreditSafe report (e.g. PT returns vatRegistrationNumber)
      const mergedVatNumber = sanitize(companyData?.vatNumber) || sanitize(partialData?.vatNumber) || sanitize(foundCompany.vatNumber) || sanitize(foundCompany.vatNo) || '';

      updateData({
        corporateCompany: {
          id: companyData?.connectId || companyData?.id || foundCompany.connectId,
          connectId: companyData?.connectId || foundCompany.connectId || '',
          name: mergedName,
          registrationNumber: mergedRegNo,
          registeredAddress: registeredAddressString,
          status: mergedStatus,
          incorporationDate: mergedIncorporationDate,
          companyType: mergedCompanyType,
          vatNumber: mergedVatNumber,
          fullAddress: {
            street: addrStreet,
            city: addrCity,
            postalCode: addrPost,
            country: addrCountry,
          },
          sicCodes: companyData?.sicCodes || [],
          mainActivity: mergedMainActivity || companyData?.mainActivity || '',
          mainActivityCode: companyData?.mainActivityCode || '',
          mainActivityEnglish: companyData?.mainActivityEnglish || '',
          directors: (companyData?.directors || []).map((d: any) => ({
            name: d.name || '',
            dateOfBirth: d.dateOfBirth || '',
            nationality: d.nationality || '',
            address: d.address || { street: '', city: '', postalCode: '', country: '' },
            appointedDate: d.appointedDate || '',
            occupation: d.occupation || 'Director',
            isManuallyAdded: false,
          })),
          shareholders: (companyData?.shareholders || []).map((s: any) => ({
            name: s.name || '',
            shareholding: s.shareholding || 0,
            shareClass: s.shareClass || 'Ordinary',
            address: s.address || { street: '', city: '', postalCode: '', country: '' },
            type: s.type || (isCompanyName(s.name) ? 'company' as const : 'individual' as const),
            ubos: [],
            registrationNumber: s.registrationNumber || '',
            isManuallyAdded: false,
            isLookedUp: false,
          })),
          countryCode: selectedCountry,
          isManualEntry: false,
        }
      });
      setIsSearching(false);
      goToNextStep();
    } catch (err) {
      console.error('Error searching company:', err);
      setSearchNotFound(true);
      setIsSearching(false);
    }
  };

  const handleContinueManually = () => {
    // Allow user to continue without CreditSafe data (sole trader or manual entry)
    updateData({
      corporateCompany: {
        id: '',
        name: '',
        registrationNumber: query.trim() || '',
        registeredAddress: '',
        status: 'Manual Entry',
        incorporationDate: '',
        companyType: '',
        fullAddress: {
          street: '',
          city: '',
          postalCode: '',
          country: selectedCountryData?.name || '',
        },
        sicCodes: [],
        directors: [],
        shareholders: [],
        countryCode: selectedCountry,
        isManualEntry: true,
      }
    });
    goToNextStep();
  };

  const handleSelectCountry = (code: string) => {
    setSelectedCountry(code);
    setShowCountryDropdown(false);
    setCountrySearch('');
    setRejectedCompany(null);
    setSearchNotFound(false);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setRejectedCompany(null);
    setSearchNotFound(false);
  };

  const isFormValid = !!selectedCountry && query.trim().length >= 2 && !rejectedCompany && !isDetectingCountry;

  // If company data was already found in a previous pass, show a confirmation
  // card so the user can continue without re-running the CreditSafe search
  const alreadyFound = Boolean(savedCompany?.registrationNumber && !savedCompany?.isManualEntry && savedCompany.name);

  if (alreadyFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full animate-fade-in-up">
          <h1 className="text-2xl sm:text-3xl font-bold text-center tracking-[0.2em] mb-2">
            {t('corporateSearch.title')}
          </h1>
          <p className="text-muted-foreground text-center text-sm mb-8">
            {t('corporateSearch.subtitle')}
          </p>

          <div className="p-5 rounded-2xl border-2 border-accent/40 bg-accent/5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Company found</p>
                <p className="font-semibold text-foreground">{savedCompany!.name}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Registration No.</p>
                <p className="font-medium">{savedCompany!.registrationNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Country</p>
                <p className="font-medium flex items-center gap-1.5">
                  <FlagImg code={savedCompany!.countryCode} size="sm" />
                  {countries.find(c => c.code === savedCompany!.countryCode)?.name || savedCompany!.countryCode}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Previously verified — you can continue or search again below.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              // Allow re-search by clearing the bypass
              updateData({ corporateCompany: undefined });
            }}
            className="mt-4 w-full py-3 text-sm text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl transition-colors"
          >
            Search for a different company
          </button>
        </div>

        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
          <div className="max-w-md mx-auto">
            <Button onClick={goToNextStep} className="w-full h-14 text-base font-medium rounded-xl">
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        <div className="animate-fade-in-up">
          <h1 className="text-2xl sm:text-3xl font-bold text-center tracking-[0.2em] mb-2">
            {t('corporateSearch.title')}
          </h1>
          <p className="text-muted-foreground text-center text-sm mb-8">
            {t('corporateSearch.subtitle')}
          </p>

          {/* Country Selection */}
          <div className="mb-6">
            <Label className="block text-sm font-medium mb-2">{t('corporateSearch.countryLabel')}</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                disabled={isDetectingCountry}
                className="w-full h-14 px-4 rounded-xl border border-border bg-card flex items-center justify-between hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {isDetectingCountry ? (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                    <span>{t('common.detectingLocation')}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <FlagImg code={selectedCountryData?.code || ''} size="md" />
                      <span className="font-medium">{selectedCountryData?.name}</span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>

              {showCountryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg z-50 max-h-64 overflow-hidden">
                  <div className="p-2 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={t('common.searchCountry')}
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="pl-9 h-10"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        onClick={() => handleSelectCountry(country.code)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-accent/50 transition-colors text-left"
                      >
                        <FlagImg code={country.code} size="sm" />
                        <span className="flex-1">{country.name}</span>
                        {selectedCountry === country.code && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Smart single-field query — auto-classifies into name / regNo / vatNo */}
          <div className="mb-6">
            <Label className="block text-sm font-medium mb-2">{t('corporateSearch.queryLabel')}</Label>
            <div className="relative">
              <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('corporateSearch.queryPlaceholder')}
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                className={`pl-12 h-14 text-lg ${rejectedCompany ? 'border-destructive' : ''}`}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('corporateSearch.queryHint')}
            </p>
          </div>

          {/* Company Not Found Warning */}
          {searchNotFound && (
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 mb-6 animate-fade-in-up">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">
                    {t('corporateSearch.notFound.title')}
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('corporateSearch.notFound.message', { regNo: query, country: selectedCountryData?.name })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('corporateSearch.notFound.hint')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Rejected Company Warning */}
          {rejectedCompany && (
            <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/30 mb-6 animate-fade-in-up">
              <div className="flex items-start gap-3">
                <XCircle className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-destructive mb-1">
                    {t('corporateSearch.rejected.title')}
                  </p>
                  <p className="text-sm mb-3">
                    {t('corporateSearch.rejected.message', { name: rejectedCompany.name, regNo: rejectedCompany.registrationNumber })}
                  </p>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-destructive/10">
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    <span className="text-sm font-medium text-destructive">
                      {t('corporateSearch.rejected.status', { status: rejectedCompany.status })}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    {t('corporateSearch.rejected.hint')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Info Box - only show if no rejection and no search failure */}
          {!rejectedCompany && !searchNotFound && (
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">{t('corporateSearch.verificationTitle')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('corporateSearch.verificationDesc')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sole trader / Manual entry option - show unless company is rejected */}
          {!rejectedCompany && (
            <button
              type="button"
              onClick={handleContinueManually}
              className={`w-full p-4 rounded-xl border border-dashed border-border hover:border-primary/50 hover:bg-primary/5 transition-all group ${!searchNotFound ? 'mt-4' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                  <Building2 className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium group-hover:text-primary transition-colors">{t('corporateSearch.manualEntryTitle')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('corporateSearch.manualEntryDesc')}
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleSearch}
            disabled={!isFormValid || isSearching}
            className="w-full h-14 text-base font-medium rounded-xl"
          >
            {isSearching ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('corporateSearch.searching')}
              </>
            ) : rejectedCompany ? (
              t('corporateSearch.findCompany')
            ) : (
              t('corporateSearch.findCompany')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
